import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FingerprintIcon, EyeIcon, EyeOffIcon, SmartphoneIcon, LockIcon, ShieldIcon, ScanFaceIcon, CheckCircle2Icon } from 'lucide-react';
interface LoginScreenProps {
  onLogin: () => void;
  onNavigateToSignup: () => void;
}
let loginBiometricCounter = 0;
export function LoginScreen({
  onLogin,
  onNavigateToSignup
}: LoginScreenProps) {
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginPhase, setLoginPhase] = useState<'idle' | 'scanning' | 'verified'>('idle');
  const [scanType, setScanType] = useState<'thumb' | 'face'>('thumb');
  const handleLogin = () => {
    const currentType = loginBiometricCounter % 2 === 0 ? 'thumb' : 'face';
    setScanType(currentType);
    loginBiometricCounter++;
    setLoginPhase('scanning');
    // Simulate biometric scan
    setTimeout(() => {
      setLoginPhase('verified');
      // Complete login after showing verified
      setTimeout(() => {
        setLoginPhase('idle');
        onLogin();
      }, 800);
    }, 2000);
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="w-full min-h-screen flex flex-col items-center justify-between py-10 px-6 relative overflow-hidden" style={{
    backgroundColor: '#1a1008'
  }} data-id="element-316">
      {/* Animated Ombre Background */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" data-id="element-317">
        {/* Large maroon blob - top left */}
        <motion.div className="absolute rounded-full" style={{
        width: 400,
        height: 400,
        left: '-15%',
        top: '-10%',
        background: 'radial-gradient(circle, #661806 0%, transparent 70%)',
        filter: 'blur(60px)'
      }} animate={{
        x: [0, 80, -20, 100, 40, 0],
        y: [0, 60, 100, 30, 80, 0],
        scale: [1, 1.25, 0.85, 1.2, 0.95, 1],
        opacity: [0.8, 0.6, 0.9, 0.5, 0.85, 0.8],
        rotate: [0, 15, -10, 20, -5, 0]
      }} transition={{
        duration: 14,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-318" />

        {/* Dark blob - center right */}
        <motion.div className="absolute rounded-full" style={{
        width: 350,
        height: 350,
        right: '-20%',
        top: '20%',
        background: 'radial-gradient(circle, #303030 0%, transparent 70%)',
        filter: 'blur(50px)'
      }} animate={{
        x: [0, -70, 20, -80, -30, 0],
        y: [0, 50, -30, 70, 10, 0],
        scale: [1, 0.8, 1.15, 0.85, 1.1, 1],
        opacity: [0.7, 0.5, 0.8, 0.4, 0.7, 0.7],
        rotate: [0, -20, 10, -15, 5, 0]
      }} transition={{
        duration: 18,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: 0.5
      }} data-id="element-319" />

        {/* Warm beige glow - center (pulsing) */}
        <motion.div className="absolute rounded-full" style={{
        width: 300,
        height: 300,
        left: '20%',
        top: '35%',
        background: 'radial-gradient(circle, #FFF7F3 0%, transparent 70%)',
        filter: 'blur(70px)'
      }} animate={{
        x: [0, 50, -40, 30, -20, 0],
        y: [0, -40, 30, -50, 20, 0],
        scale: [1, 1.4, 0.7, 1.3, 0.8, 1],
        opacity: [0.12, 0.2, 0.06, 0.18, 0.08, 0.12]
      }} transition={{
        duration: 12,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: 1
      }} data-id="element-320" />

        {/* Deep maroon blob - bottom */}
        <motion.div className="absolute rounded-full" style={{
        width: 450,
        height: 450,
        left: '30%',
        bottom: '-15%',
        background: 'radial-gradient(circle, #661806 0%, #303030 50%, transparent 70%)',
        filter: 'blur(65px)'
      }} animate={{
        x: [0, -60, 50, -30, 40, 0],
        y: [0, -50, 20, -60, 10, 0],
        scale: [1, 1.15, 0.85, 1.1, 0.9, 1],
        opacity: [0.6, 0.4, 0.7, 0.35, 0.65, 0.6],
        rotate: [0, 10, -8, 12, -5, 0]
      }} transition={{
        duration: 20,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: 0.3
      }} data-id="element-321" />

        {/* Small accent glow - top right */}
        <motion.div className="absolute rounded-full" style={{
        width: 180,
        height: 180,
        right: '5%',
        top: '5%',
        background: 'radial-gradient(circle, #8A2510 0%, transparent 70%)',
        filter: 'blur(40px)'
      }} animate={{
        x: [0, -40, 25, -50, 15, 0],
        y: [0, 35, -25, 40, -10, 0],
        scale: [1, 1.3, 0.8, 1.25, 0.85, 1],
        opacity: [0.5, 0.3, 0.6, 0.25, 0.55, 0.5]
      }} transition={{
        duration: 10,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: 2
      }} data-id="element-322" />

        {/* Rotating gradient ring */}
        <motion.div className="absolute" style={{
        width: 500,
        height: 500,
        left: '50%',
        top: '50%',
        marginLeft: -250,
        marginTop: -250,
        borderRadius: '50%',
        border: '2px solid transparent',
        backgroundImage: 'conic-gradient(from 0deg, transparent, #661806, transparent, #FFF7F3, transparent, #661806, transparent)',
        backgroundOrigin: 'border-box',
        backgroundClip: 'border-box',
        opacity: 0.08,
        filter: 'blur(8px)'
      }} animate={{
        rotate: [0, 360],
        scale: [0.9, 1.1, 0.9]
      }} transition={{
        rotate: {
          duration: 30,
          repeat: Infinity,
          ease: 'linear'
        },
        scale: {
          duration: 15,
          repeat: Infinity,
          ease: 'easeInOut'
        }
      }} data-id="element-323" />

        {/* Warm streak - moving right */}
        <motion.div className="absolute" style={{
        width: 500,
        height: 120,
        left: '-10%',
        top: '55%',
        background: 'linear-gradient(90deg, transparent, #661806, #FFF7F3, #661806, transparent)',
        filter: 'blur(30px)',
        borderRadius: '50%'
      }} animate={{
        x: [0, 120, -60, 100, 0],
        rotate: [0, 8, -5, 6, 0],
        opacity: [0.06, 0.12, 0.03, 0.1, 0.06]
      }} transition={{
        duration: 16,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: 1
      }} data-id="element-324" />

        {/* Cool streak - moving left */}
        <motion.div className="absolute" style={{
        width: 400,
        height: 80,
        right: '-5%',
        top: '25%',
        background: 'linear-gradient(90deg, transparent, #303030, #661806, #303030, transparent)',
        filter: 'blur(25px)',
        borderRadius: '50%'
      }} animate={{
        x: [0, -100, 40, -80, 0],
        rotate: [0, -6, 4, -5, 0],
        opacity: [0.05, 0.1, 0.03, 0.08, 0.05]
      }} transition={{
        duration: 20,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: 3
      }} data-id="element-325" />

        {/* Noise/grain overlay for texture */}
        <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: 'radial-gradient(circle, #FFF7F3 1px, transparent 1px)',
        backgroundSize: '24px 24px'
      }} data-id="element-326" />
      </div>

      {/* Top Section — Logo */}
      <div className="flex flex-col items-center pt-12 w-full relative z-10" data-id="element-327">
        <motion.img initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.1
      }} src="https://cdn.magicpatterns.com/uploads/k5mzauqmFki77bNt7CFYh1/Whatsapp_digiBOP-removebg-preview.png" alt="digiBOP Logo" className="w-48 h-auto mb-16 drop-shadow-lg" data-id="element-328" />
      </div>

      {/* Center Section — Glass Form Card */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5,
      delay: 0.2
    }} className="w-full max-w-sm relative z-10 rounded-[28px] p-6 space-y-6" style={{
      background: 'rgba(255, 247, 243, 0.08)',
      backdropFilter: 'blur(24px)',
      WebkitBackdropFilter: 'blur(24px)',
      border: '1px solid rgba(255, 247, 243, 0.12)',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 247, 243, 0.1)'
    }} data-id="element-329">
        {/* Mobile Number */}
        <div data-id="element-330">
          <label className="block text-xs font-bold text-[#FFF7F3]/60 uppercase tracking-wider mb-2 ml-1" data-id="element-331">
            Mobile Number
          </label>
          <div className="flex items-center rounded-full px-5 py-4 transition-all" style={{
          background: 'rgba(255, 247, 243, 0.06)',
          border: '1px solid rgba(255, 247, 243, 0.1)'
        }} data-id="element-332">
            <SmartphoneIcon className="w-5 h-5 text-[#FFF7F3]/50 mr-3 flex-shrink-0" strokeWidth={2.5} data-id="element-333" />
            <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="0000 000 0000" className="flex-1 outline-none text-base font-medium text-[#FFF7F3] bg-transparent placeholder:text-[#FFF7F3]/30" data-id="element-334" />
          </div>
        </div>

        {/* Secure MPIN */}
        <div data-id="element-335">
          <div className="flex items-center justify-between mb-2 ml-1 mr-1" data-id="element-336">
            <label className="block text-xs font-bold text-[#FFF7F3]/60 uppercase tracking-wider" data-id="element-337">
              Secure MPIN
            </label>
            <button className="text-xs font-bold text-[#F37024] uppercase tracking-wider hover:opacity-80 transition-opacity" data-id="element-338">
              Forgot PIN?
            </button>
          </div>
          <div className="flex items-center rounded-full px-5 py-4 transition-all" style={{
          background: 'rgba(255, 247, 243, 0.06)',
          border: '1px solid rgba(255, 247, 243, 0.1)'
        }} data-id="element-339">
            <LockIcon className="w-5 h-5 text-[#FFF7F3]/50 mr-3 flex-shrink-0" strokeWidth={2.5} data-id="element-340" />
            <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••" className="flex-1 outline-none text-base font-medium tracking-widest text-[#FFF7F3] bg-transparent placeholder:text-[#FFF7F3]/30" data-id="element-341" />
            <button onClick={() => setShowPassword(!showPassword)} className="ml-2 flex-shrink-0" data-id="element-342">
              {showPassword ? <EyeOffIcon className="w-5 h-5 text-[#FFF7F3]/50" strokeWidth={2.5} data-id="element-343" /> : <EyeIcon className="w-5 h-5 text-[#FFF7F3]/50" strokeWidth={2.5} data-id="element-344" />}
            </button>
          </div>
        </div>

        {/* Login Button */}
        <div className="pt-4" data-id="element-345">
          <button onClick={handleLogin} className="w-full bg-gradient-to-r from-[#D95A1A] to-[#B84A12] text-white py-4 rounded-full font-bold text-lg tracking-wide shadow-[0_8px_24px_rgba(217,90,26,0.4)] hover:shadow-[0_8px_30px_rgba(217,90,26,0.5)] active:scale-[0.98] transition-all" data-id="element-346">
            LOGIN
          </button>
        </div>

        {/* Biometric Login Button */}
        <div className="flex flex-col items-center pt-6 pb-2" data-id="element-347">
          <span className="text-[10px] font-bold text-[#FFF7F3]/30 uppercase tracking-wider mb-3" data-id="element-348">
            Biometric Login
          </span>
          <div className="flex items-center gap-5" data-id="element-349">
            <button onClick={handleLogin} disabled={loginPhase !== 'idle'} className="flex flex-col items-center gap-2 group disabled:opacity-50" data-id="element-350">
              <div className="w-14 h-14 rounded-full flex items-center justify-center transition-shadow group-hover:shadow-lg group-active:scale-95" style={{
              background: 'rgba(255, 247, 243, 0.08)',
              border: '1px solid rgba(255, 247, 243, 0.15)',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)'
            }} data-id="element-351">
                <FingerprintIcon className="w-7 h-7 text-[#F37024]" strokeWidth={1.5} data-id="element-352" />
              </div>
              <span className="text-[10px] font-bold text-[#FFF7F3]/40 uppercase tracking-wider" data-id="element-353">
                Fingerprint
              </span>
            </button>
            <div className="w-px h-10 bg-[#FFF7F3]/10" data-id="element-354" />
            <button onClick={handleLogin} disabled={loginPhase !== 'idle'} className="flex flex-col items-center gap-2 group disabled:opacity-50" data-id="element-355">
              <div className="w-14 h-14 rounded-full flex items-center justify-center transition-shadow group-hover:shadow-lg group-active:scale-95" style={{
              background: 'rgba(255, 247, 243, 0.08)',
              border: '1px solid rgba(255, 247, 243, 0.15)',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)'
            }} data-id="element-356">
                <ScanFaceIcon className="w-7 h-7 text-[#F37024]" strokeWidth={1.5} data-id="element-357" />
              </div>
              <span className="text-[10px] font-bold text-[#FFF7F3]/40 uppercase tracking-wider" data-id="element-358">
                Face Lock
              </span>
            </button>
          </div>
        </div>
      </motion.div>

      {/* Biometric Scanning Overlay */}
      <AnimatePresence data-id="element-359">
        {loginPhase !== 'idle' && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex flex-col items-center justify-center" style={{
        background: 'rgba(26, 16, 8, 0.92)',
        backdropFilter: 'blur(20px)'
      }} data-id="element-360">
            <AnimatePresence mode="wait" data-id="element-361">
              {loginPhase === 'scanning' && <motion.div key="scanning" initial={{
            opacity: 0,
            scale: 0.8
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.8
          }} className="flex flex-col items-center gap-6" data-id="element-362">
                  <motion.div animate={{
              scale: [1, 1.2, 1],
              opacity: [0.6, 1, 0.6]
            }} transition={{
              duration: 1.2,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="w-24 h-24 rounded-full flex items-center justify-center" style={{
              background: 'rgba(243, 112, 36, 0.15)',
              border: '2px solid rgba(243, 112, 36, 0.3)'
            }} data-id="element-363">
                    {scanType === 'thumb' ? <FingerprintIcon className="w-12 h-12 text-[#F37024]" strokeWidth={1.5} data-id="element-364" /> : <ScanFaceIcon className="w-12 h-12 text-[#F37024]" strokeWidth={1.5} data-id="element-365" />}
                  </motion.div>
                  <div className="text-center" data-id="element-366">
                    <p className="text-[18px] font-bold text-[#FFF7F3] mb-1" data-id="element-367">
                      {scanType === 'thumb' ? 'Scanning Fingerprint' : 'Scanning Face'}
                    </p>
                    <p className="text-[13px] text-[#FFF7F3]/50" data-id="element-368">
                      {scanType === 'thumb' ? 'Place your finger on the sensor' : 'Look at the camera'}
                    </p>
                  </div>
                  {/* Scanning progress bar */}
                  <div className="w-48 h-1.5 bg-[#FFF7F3]/10 rounded-full overflow-hidden" data-id="element-369">
                    <motion.div initial={{
                width: '0%'
              }} animate={{
                width: '100%'
              }} transition={{
                duration: 2,
                ease: 'easeInOut'
              }} className="h-full bg-gradient-to-r from-[#F37024] to-[#E8943A] rounded-full" data-id="element-370" />
                  </div>
                </motion.div>}

              {loginPhase === 'verified' && <motion.div key="verified" initial={{
            opacity: 0,
            scale: 0.8
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.8
          }} className="flex flex-col items-center gap-6" data-id="element-371">
                  <motion.div initial={{
              scale: 0.3
            }} animate={{
              scale: 1
            }} transition={{
              type: 'spring',
              damping: 12,
              stiffness: 200
            }} className="w-24 h-24 rounded-full bg-[#22C55E]/15 flex items-center justify-center" style={{
              border: '2px solid rgba(34, 197, 94, 0.3)'
            }} data-id="element-372">
                    <CheckCircle2Icon className="w-12 h-12 text-[#22C55E]" strokeWidth={1.5} data-id="element-373" />
                  </motion.div>
                  <div className="text-center" data-id="element-374">
                    <p className="text-[18px] font-bold text-[#FFF7F3] mb-1" data-id="element-375">
                      Verified Successfully
                    </p>
                    <p className="text-[13px] text-[#FFF7F3]/50" data-id="element-376">
                      Logging you in...
                    </p>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </motion.div>}
      </AnimatePresence>

      {/* Bottom Section */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.5,
      delay: 0.4
    }} className="flex flex-col items-center gap-8 w-full max-w-sm relative z-10" data-id="element-377">
        <p className="text-sm text-[#FFF7F3]/60" data-id="element-378">
          Don't have an account?{' '}
          <button onClick={onNavigateToSignup} className="font-bold text-[#F37024] hover:underline" data-id="element-379">
            Register now
          </button>
        </p>

        <div className="flex items-center gap-1.5 opacity-40" data-id="element-380">
          <ShieldIcon className="w-3.5 h-3.5 text-[#FFF7F3]" strokeWidth={2} data-id="element-381" />
          <span className="text-[9px] font-bold text-[#FFF7F3] uppercase tracking-wider" data-id="element-382"></span>
        </div>
      </motion.div>
    </motion.div>;
}